import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import React, { useMemo, useState } from 'react';
import type { BankLine } from '../../data/types';
import { date, money } from '../../lib/format';
import { useRegistry } from '../../store/RegistryStore';
import { useTenantScope } from '../../store/selectors';
import { useToast } from '../../ui/feedback';

interface Candidate {
  key: string;
  label: string;
  callId?: string;
  holderId?: string;
  applicationId?: string;
  amount: number;
}

export default function MatchDialog({
  line,
  onClose,
}: {
  line: BankLine | null;
  onClose: () => void;
}) {
  const scope = useTenantScope();
  const { dispatch } = useRegistry();
  const toast = useToast();
  const [choice, setChoice] = useState('');

  const candidates = useMemo<Candidate[]>(() => {
    if (!line) return [];
    const fromCalls = scope.calls
      .filter((call) => call.fundId === line.fundId && call.status === 'ISSUED')
      .flatMap((call) =>
        call.lines
          .filter((item) => item.status !== 'PAID')
          .map((item) => ({
            key: `${call.id}|${item.holderId}`,
            label: `Call ${call.number} · ${scope.holderById.get(item.holderId)?.name} · ref ${item.crn}`,
            callId: call.id,
            holderId: item.holderId,
            amount: item.amount - item.paid,
          })),
      );
    const fromApplications = scope.applications
      .filter(
        (application) =>
          application.fundId === line.fundId &&
          application.checklist.funds !== 'DONE' &&
          !['REJECTED', 'ISSUED', 'WITHDRAWN'].includes(application.status),
      )
      .map((application) => ({
        key: application.id,
        label: `${application.reference} · ${application.applicantName}`,
        applicationId: application.id,
        amount: application.amount,
      }));
    return [...fromApplications, ...fromCalls].sort(
      (left, right) => Math.abs(left.amount - line.amount) - Math.abs(right.amount - line.amount),
    );
  }, [line, scope]);

  const close = () => {
    setChoice('');
    onClose();
  };

  return (
    <Dialog open={Boolean(line)} onClose={close} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>Match bank receipt</DialogTitle>
      {line && (
        <DialogContent>
          <Typography variant="body2">
            {date(line.date)} · <strong>{money(line.amount, true)}</strong> · {line.description} ·
            ref {line.reference}
          </Typography>
          <TextField
            select
            fullWidth
            label="Match to"
            value={choice}
            onChange={(event) => setChoice(event.target.value)}
            sx={{ mt: 2 }}
            helperText="Closest amounts first"
          >
            {candidates.map((candidate) => (
              <MenuItem key={candidate.key} value={candidate.key}>
                {candidate.label} — {money(candidate.amount)}
                {Math.abs(candidate.amount - line.amount) < 0.01 ? ' (exact)' : ''}
              </MenuItem>
            ))}
          </TextField>
          {candidates.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Nothing open in this fund. Exclude the line if it is not a registry receipt.
            </Typography>
          )}
        </DialogContent>
      )}
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          color="inherit"
          onClick={() => {
            if (line) dispatch({ type: 'BANK_LINE_IGNORE', lineId: line.id });
            toast('Line excluded from registry matching', 'info');
            close();
          }}
        >
          Exclude
        </Button>
        <Button onClick={close}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!choice}
          onClick={() => {
            const candidate = candidates.find((item) => item.key === choice);
            if (line && candidate) {
              dispatch({
                type: 'BANK_LINE_MATCH',
                lineId: line.id,
                callId: candidate.callId,
                holderId: candidate.holderId,
                applicationId: candidate.applicationId,
              });
              toast('Receipt matched and posted');
            }
            close();
          }}
        >
          Match
        </Button>
      </DialogActions>
    </Dialog>
  );
}
